const state = new WeakMap();
const shown = new WeakMap();
let frame = 0;

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

const getState = (root) => {
  if (!state.has(root)) state.set(root, { active: '', lockUntil: 0 });
  return state.get(root);
};

const headerInfo = () => {
  const header = document.querySelector('#header-component');
  if (!header) return { mode: '', height: 0 };
  const height = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || header.offsetHeight;
  return { mode: header.getAttribute('sticky') || '', height };
};

const scrollToTarget = (root, target, smooth) => {
  root.style.removeProperty('--drv-faqp-offset');
  const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  const goingUp = target.getBoundingClientRect().top < margin;
  const { mode, height } = headerInfo();
  if (mode === 'always' || (mode === 'scroll-up' && goingUp)) {
    root.style.setProperty('--drv-faqp-offset', `${margin + height}px`);
  }
  target.scrollIntoView({ behavior: smooth && !reducedMotion() ? 'smooth' : 'auto', block: 'start' });
};

const revealPill = (root, id) => {
  const scroller = root.querySelector('[data-drv-faqp-bar-pills]');
  if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
  const pill = scroller.querySelector(`[data-drv-faqp-pill="${CSS.escape(id)}"]`);
  if (!pill) return;
  const delta = pill.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
  scroller.scrollBy({ left: delta - (scroller.clientWidth - pill.offsetWidth) / 2, behavior: reducedMotion() ? 'auto' : 'smooth' });
};

const setActive = (root, id) => {
  const current = getState(root);
  if (current.active === id) return;
  current.active = id;
  root.querySelectorAll('[data-drv-faqp-pill]').forEach((pill) => {
    if (pill.dataset.drvFaqpPill === id) pill.setAttribute('aria-current', 'true');
    else pill.removeAttribute('aria-current');
  });
  revealPill(root, id);
};

const update = (root) => {
  const groups = [...root.querySelectorAll('[data-drv-faqp-group]')];
  if (!groups.length) return;
  const bar = root.querySelector('[data-drv-faqp-bar]');
  const tabs = root.querySelector('[data-drv-faqp-tabs]');
  let barBottom = 0;
  let barHeight = 0;
  if (bar && tabs) {
    const barTop = parseFloat(getComputedStyle(bar).top) || 0;
    barHeight = bar.firstElementChild?.offsetHeight || 0;
    const barEnd = barTop + barHeight;
    const show = tabs.getBoundingClientRect().bottom <= barEnd && root.getBoundingClientRect().bottom > barEnd;
    bar.classList.toggle('is-visible', show);
    bar.inert = !show;
    if (show) barBottom = barEnd;
  }
  if (getState(root).lockUntil > performance.now()) return;
  const gap = Math.max((parseFloat(getComputedStyle(groups[0]).scrollMarginTop) || 0) - barHeight, 0);
  const line = barBottom + gap * 1.5;
  let active = groups[0];
  groups.forEach((group) => {
    if (group.getBoundingClientRect().top <= line) active = group;
  });
  setActive(root, active.id);
};

const updateAll = () => {
  frame = 0;
  document.querySelectorAll('[data-drv-faqp]').forEach(update);
};

const schedule = () => {
  if (!frame) frame = requestAnimationFrame(updateAll);
};

const perGroup = (root) => Number(root.dataset.perGroup) || 25;

const rowsOf = (group) => [...(group.querySelector('[data-drv-faqp-rows]')?.children || [])];

const applyLimit = (root, group) => {
  if (!shown.has(group)) shown.set(group, perGroup(root));
  const limit = shown.get(group);
  const rows = rowsOf(group);
  rows.forEach((row, index) => row.classList.toggle('drv-faqp__extra', index >= limit));
  const more = group.querySelector('.drv-faqp__more');
  if (more) more.hidden = rows.length <= limit;
};

const showMore = (root, button, moveFocus) => {
  const group = button.closest('[data-drv-faqp-group]');
  if (!group) return;
  const rows = rowsOf(group);
  const first = shown.get(group) || perGroup(root);
  shown.set(group, first + perGroup(root));
  applyLimit(root, group);
  if (moveFocus) rows[first]?.querySelector('summary')?.focus();
  schedule();
};

const reveal = (root, details) => {
  const group = details.closest('[data-drv-faqp-group]');
  if (!group) return;
  const index = rowsOf(group).findIndex((row) => row.contains(details));
  if (index < 0) return;
  shown.set(group, Math.max(shown.get(group) || perGroup(root), index + 1));
  applyLimit(root, group);
};

const onClick = (root, event) => {
  const more = event.target.closest('[data-drv-faqp-more]');
  if (more) {
    showMore(root, more, event.detail === 0);
    return;
  }
  const pill = event.target.closest('[data-drv-faqp-pill]');
  if (!pill) return;
  const id = pill.dataset.drvFaqpPill;
  const target = root.querySelector(`#${CSS.escape(id)}`);
  if (!target) return;
  event.preventDefault();
  getState(root).lockUntil = performance.now() + 1000;
  setActive(root, id);
  scrollToTarget(root, target, true);
  history.replaceState(null, '', `#${id}`);
  target.querySelector('.drv-faq__heading')?.focus({ preventScroll: true });
};

const openFromHash = async () => {
  let id = '';
  try {
    id = decodeURIComponent(location.hash.slice(1));
  } catch {
    return true;
  }
  if (!id) return true;
  const target = document.getElementById(id);
  const root = target?.closest('[data-drv-faqp]');
  if (!root) return false;
  if (target instanceof HTMLDetailsElement) {
    reveal(root, target);
    await customElements.whenDefined('accordion-custom');
    target.open = true;
  }
  requestAnimationFrame(() => scrollToTarget(root, target, false));
  return true;
};

const questionOf = (row) => row.querySelector('.drv-faq__q')?.textContent.trim() || '';

const sortRows = (group) => {
  const list = group.querySelector('[data-drv-faqp-rows]');
  if (!list) return;
  const rows = [...list.children];
  const sorted = [...rows].sort((a, b) => questionOf(a).localeCompare(questionOf(b), undefined, { sensitivity: 'base', numeric: true }));
  if (sorted.some((row, index) => row !== rows[index])) list.append(...sorted);
};

const addPill = (root, group) => {
  root.querySelectorAll('[data-drv-faqp-hero-pills], [data-drv-faqp-bar-pills]').forEach((list) => {
    const template = list.querySelector('li');
    if (!template) return;
    const item = template.cloneNode(true);
    const link = item.querySelector('a');
    link.href = `#${group.id}`;
    link.dataset.drvFaqpPill = group.id;
    link.removeAttribute('aria-current');
    link.textContent = group.querySelector('.drv-faq__heading')?.textContent.trim() || group.id;
    list.append(item);
  });
};

const merge = (root, incoming) => {
  const groups = root.querySelector('[data-drv-faqp-groups]');
  if (!groups) return;
  incoming.querySelectorAll('details[id]').forEach((details) => {
    let id = details.id;
    for (let n = 2; document.getElementById(id); n += 1) id = `${details.id}-${n}`;
    details.id = id;
  });
  const existing = [...groups.querySelectorAll('[data-drv-faqp-group]')].find((group) => group.id === incoming.id);
  if (!existing) {
    const group = document.importNode(incoming, true);
    groups.append(group);
    addPill(root, group);
    return;
  }
  const list = existing.querySelector('[data-drv-faqp-rows]');
  incoming.querySelectorAll('[data-drv-faqp-rows] > *').forEach((row) => list.append(document.importNode(row, true)));
};

const updateStructuredData = (root) => {
  const script = document.querySelector(`script[data-drv-faqp-ld="${CSS.escape(root.dataset.sectionId || '')}"]`);
  if (!script) return;
  let data;
  try {
    data = JSON.parse(script.textContent);
  } catch {
    return;
  }
  const faqPage = data['@graph']?.find((node) => node['@type'] === 'FAQPage');
  if (!faqPage) return;
  faqPage.mainEntity = [...root.querySelectorAll('[data-drv-faqp-rows] details')].map((details) => {
    const answer = details.querySelector('.metafield-rich_text_field') || details.querySelector('.drv-faq__answer');
    return { '@type': 'Question', name: questionOf(details), acceptedAnswer: { '@type': 'Answer', text: answer ? answer.innerHTML.trim() : '' } };
  });
  script.textContent = JSON.stringify(data);
};

const loadRest = async (root, hashFound) => {
  let next = root.querySelector('[data-drv-faqp-groups]')?.dataset.next;
  if (!next) return;
  for (let pages = 0; next && pages < 10; pages += 1) {
    const url = new URL(next, location.origin);
    url.searchParams.set('section_id', root.dataset.sectionId);
    let html = '';
    try {
      const response = await fetch(url);
      if (!response.ok) break;
      html = await response.text();
    } catch {
      break;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('[data-drv-faqp-group]').forEach((incoming) => merge(root, incoming));
    next = doc.querySelector('[data-drv-faqp-groups]')?.dataset.next || '';
  }
  root.querySelectorAll('[data-drv-faqp-group]').forEach((group) => {
    sortRows(group);
    applyLimit(root, group);
  });
  updateStructuredData(root);
  schedule();
  if (!hashFound) openFromHash();
};

const init = () => {
  document.querySelectorAll('[data-drv-faqp]').forEach((root) => {
    if (state.has(root)) return;
    getState(root);
    const bar = root.querySelector('[data-drv-faqp-bar]');
    if (bar) {
      bar.inert = true;
      bar.hidden = false;
    }
    root.querySelectorAll('[data-drv-faqp-group]').forEach((group) => applyLimit(root, group));
    root.addEventListener('click', (event) => onClick(root, event));
    openFromHash().then((hashFound) => loadRest(root, hashFound));
  });
  schedule();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
document.addEventListener('scroll', schedule, { capture: true, passive: true });
window.addEventListener('resize', schedule);
window.addEventListener('hashchange', openFromHash);
document.addEventListener('shopify:section:load', init);
